import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Tag, Modal, message, 
  Input, Select, Form, Card, Statistic, Progress,
  Switch, InputNumber, DatePicker, Tabs, Row, Col
} from 'antd';
import { 
  EditOutlined, DeleteOutlined, BarChartOutlined,
  UsergroupAddOutlined, TrophyOutlined, DollarOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import apiService from '../services/apiService';

const { TabPane } = Tabs;
// // const { RangePicker } // Not used // Not used = DatePicker;

const Rewards = () => {
  // États
  const [activeTab, setActiveTab] = useState('1');
  const [rewardLevels, setRewardLevels] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: undefined,
    dateRange: []
  });

  // Fetch des données
  const fetchRewardLevels = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        apiService.admin.getRewardLevels(),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setRewardLevels(response.data.levels);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des niveaux');
    }
    setLoading(false);
  };

  const fetchPayments = async (params = {}) => {
    setLoading(true);
    try {
      const { current, pageSize } = params;
      const { status } = filters;

      const response = await axios.get(
        apiService.admin.getRewardPayments({
          page: current,
          limit: pageSize,
          status
        }), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setPayments(response.data.payments);
      setPagination({
        ...pagination,
        total: response.data.total,
        current: response.data.currentPage
      });
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des paiements');
    }
    setLoading(false);
  };

  const fetchPaymentStats = async () => {
    try {
      const response = await axios.get(
        apiService.admin.getPaymentStats(),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setPaymentStats(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des statistiques');
    }
  };

  useEffect(() => {
    if (activeTab === '1') {
      fetchRewardLevels();
    } else {
      fetchPayments({ current: 1, pageSize: 10 });
      fetchPaymentStats();
    }
  }, [activeTab, filters]);

  // Handlers pour les niveaux de récompense
  const handleCreateLevel = async (values) => {
    try {
      await axios.post(
        apiService.admin.getRewardLevels(),
        values,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Niveau créé avec succès');
      setModalVisible(false);
      fetchRewardLevels();
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la création');
    }
  };

  const handleUpdateLevel = async (values) => {
    try {
      await axios.put(
        apiService.admin.updateRewardLevel(selectedItem._id),
        values,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Niveau mis à jour avec succès');
      setModalVisible(false);
      fetchRewardLevels();
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteLevel = async (levelId) => {
    try {
      await axios.delete(
        apiService.admin.deleteRewardLevel(levelId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Niveau supprimé avec succès');
      fetchRewardLevels();
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  // Handler pour les paiements
  const handleUpdatePaymentStatus = async (paymentId, status) => {
    try {
      await axios.patch(
        apiService.admin.updatePaymentStatus(paymentId),
        { status },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Statut mis à jour avec succès');
      fetchPayments(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Colonnes pour les tableaux
  const levelColumns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Tag color={
          name === 'LEGENDARY' ? 'purple' :
          name === 'PLATINUM' ? 'blue' :
          name === 'GOLD' ? 'gold' :
          name === 'SILVER' ? 'default' :
          'green'
        }>
          {name}
        </Tag>
      )
    },
    {
      title: 'Points requis',
      key: 'points',
      render: (_, record) => (
        <span>
          {record.minPoints} - {record.maxPoints || '∞'}
        </span>
      )
    },
    {
      title: 'Sponsors',
      dataIndex: 'sponsorCount',
      key: 'sponsorCount'
    },
    {
      title: 'Invitation requise',
      dataIndex: 'requiresInvitation',
      key: 'requiresInvitation',
      render: (required) => (
        <Tag color={required ? 'blue' : 'green'}>
          {required ? 'Oui' : 'Non'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedItem(record);
              form.setFieldsValue(record);
              setModalVisible(true);
            }}
          />
          <Button 
            icon={<DeleteOutlined />}
            danger
            onClick={() => {
              Modal.confirm({
                title: 'Êtes-vous sûr de vouloir supprimer ce niveau ?',
                content: 'Cette action est irréversible.',
                okText: 'Oui',
                cancelText: 'Non',
                onOk: () => handleDeleteLevel(record._id)
              });
            }}
          />
        </Space>
      )
    }
  ];

  const paymentColumns = [
    {
      title: 'Référence',
      dataIndex: 'reference',
      key: 'reference'
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `${amount} €`
    },
    {
      title: 'Utilisateur',
      dataIndex: ['user', 'email'],
      key: 'user'
    },
    {
      title: 'Pack',
      dataIndex: ['pack', 'name'],
      key: 'pack'
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={
          status === 'completed' ? 'success' :
          status === 'pending' ? 'processing' :
          'error'
        }>
          {status}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <>
              <Button
                icon={<CheckCircleOutlined />}
                type="primary"
                onClick={() => handleUpdatePaymentStatus(record._id, 'completed')}
              >
                Valider
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                danger
                onClick={() => handleUpdatePaymentStatus(record._id, 'failed')}
              >
                Rejeter
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Niveaux de récompense" key="1">
          <div className="flex justify-between mb-4">
            <Button
              type="primary"
              icon={<TrophyOutlined />}
              onClick={() => {
                setSelectedItem(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Nouveau niveau
            </Button>
          </div>

          <Table
            columns={levelColumns}
            dataSource={rewardLevels}
            rowKey="_id"
            loading={loading}
          />
        </TabPane>

        <TabPane tab="Paiements" key="2">
          {paymentStats && (
            <Row gutter={[16, 16]} className="mb-6">
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total des paiements"
                    value={paymentStats.globalStats.totalAmount}
                    prefix="€"
                    precision={2}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Nombre de transactions"
                    value={paymentStats.globalStats.totalPayments}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Montant moyen"
                    value={paymentStats.globalStats.averageAmount}
                    prefix="€"
                    precision={2}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Taux de succès"
                    value={(paymentStats.globalStats.successfulPayments / paymentStats.globalStats.totalPayments) * 100}
                    suffix="%"
                    precision={2}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <div className="mb-4">
            <Space>
              <Select
                placeholder="Statut"
                style={{ width: 200 }}
                allowClear
                onChange={(value) => setFilters({ ...filters, status: value })}
              >
                <Select.Option value="completed">Complété</Select.Option>
                <Select.Option value="pending">En attente</Select.Option>
                <Select.Option value="failed">Échoué</Select.Option>
              </Select>
            </Space>
          </div>

          <Table
            columns={paymentColumns}
            dataSource={payments}
            rowKey="_id"
            pagination={pagination}
            loading={loading}
            onChange={(newPagination) => fetchPayments(newPagination)}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={selectedItem ? 'Modifier le niveau' : 'Créer un niveau'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={selectedItem ? handleUpdateLevel : handleCreateLevel}
        >
          <Form.Item
            name="name"
            label="Nom"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="EMERGENT">Émergent</Select.Option>
              <Select.Option value="SILVER">Silver</Select.Option>
              <Select.Option value="GOLD">Gold</Select.Option>
              <Select.Option value="PLATINUM">Platinum</Select.Option>
              <Select.Option value="LEGENDARY">Legendary</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="minPoints"
            label="Points minimum"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} className="w-full" />
          </Form.Item>

          <Form.Item
            name="maxPoints"
            label="Points maximum"
          >
            <InputNumber min={0} className="w-full" />
          </Form.Item>

          <Form.Item
            name="benefits"
            label="Avantages"
          >
            <Select mode="tags" />
          </Form.Item>

          <Form.Item
            name="requiresInvitation"
            label="Nécessite une invitation"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <div className="flex justify-end gap-3">
            <Button onClick={() => setModalVisible(false)}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit">
              {selectedItem ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Rewards;